import {Component, inject, OnInit} from '@angular/core';
import {AccessService} from "../../services/access.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {DataService} from "../../services/data.service";
import {ToastrService} from "ngx-toastr";
import Swal from "sweetalert2";
import {ViewMode} from "../../interfaces";
import {NgIf} from "@angular/common";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";

@Component({
    selector: 'app-generic-list-detail',
    imports: [NgIf, ReactiveFormsModule, FormsModule],
    styles: `
        .drag-over {
            background-color: #e8f0fe;
            outline: 2px dashed var(--primary);
        }
    `,
    template: `
        <div class="card">
            <div class="card-body">
                <div class="mb-4">
                    <h3>
                        <i class="fa-solid fa-rectangle-list me-2"></i>
                        @switch (viewMode) {
                            @case (ViewMode.INSERT) {
                                Liste anlegen
                            }
                            @case (ViewMode.EDIT) {
                                Liste bearbeiten
                            }
                            @case (ViewMode.VIEW) {
                                Liste
                            }
                        }
                    </h3>
                </div>

                <form [formGroup]="form">
                    <div class="row mb-2">
                        <div class="col">
                            <label>Name</label>
                            <input type="text" class="form-control" formControlName="name" *ngIf="viewMode != ViewMode.VIEW">
                            <p *ngIf="viewMode == ViewMode.VIEW">{{ form.value.name }}</p>
                        </div>
                    </div>
                    <div class="row mb-2">
                        <div class="col">
                            <label>Header</label>
                            <textarea class="form-control" formControlName="header" *ngIf="viewMode != ViewMode.VIEW"></textarea>
                            <p *ngIf="viewMode == ViewMode.VIEW" style="white-space: pre-wrap">{{ form.value.header }}
                                <span class="text-muted" *ngIf="!form.value.header">Nicht gesetzt</span>
                            </p>
                        </div>
                        <div class="col">
                            <label>Footer</label>
                            <textarea class="form-control" formControlName="footer" *ngIf="viewMode != ViewMode.VIEW"></textarea>
                            <p *ngIf="viewMode == ViewMode.VIEW" style="white-space: pre-wrap">{{ form.value.footer }}
                                <span class="text-muted" *ngIf="!form.value.footer">Nicht gesetzt</span>
                            </p>
                        </div>
                    </div>
                </form>

                <label class="form-label">Spaltenkonfiguration</label>
                <div class="table-responsive">
                    <table class="table table-sm">
                        <thead>
                        <tr>
                            @if (viewMode != ViewMode.VIEW) {
                                <th style="width: 30px"></th>
                            }
                            <th style="width: 30px">#</th>
                            <th>Name</th>
                            <th>Typ</th>
                            @if (viewMode != ViewMode.VIEW) {
                                <th style="width: 100px"></th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                            @for (col of columns; track col; let i = $index) {
                                @if (!col._deleted) {
                                    <tr [attr.draggable]="viewMode != ViewMode.VIEW ? true : null"
                                        (dragstart)="onColumnDragStart($event, i)"
                                        (dragover)="onColumnDragOver($event, i)"
                                        (dragleave)="onColumnDragLeave()"
                                        (drop)="onColumnDrop($event, i)"
                                        [class.drag-over]="dragOverIndex === i">
                                        @if (viewMode != ViewMode.VIEW) {
                                            <td style="cursor: grab"><i class="fa fa-grip-vertical text-muted"></i></td>
                                        }
                                        <td>{{ col.position}}</td>
                                        <td>
                                            <input *ngIf="col._editing" type="text" class="form-control form-control-sm" [(ngModel)]="col.name" placeholder="Spaltenname">
                                            <span *ngIf="!col._editing">{{ col.name }}</span>
                                        </td>
                                        <td>
                                            <select *ngIf="col._editing" class="form-select form-select-sm" [(ngModel)]="col.type">
                                                <option value="text">Text</option>
                                                <option value="number">Zahl</option>
                                                <option value="image">Bild</option>
                                            </select>
                                            <span *ngIf="!col._editing">{{ getColumnTypeLabel(col.type) }}</span>
                                        </td>
                                        @if (viewMode != ViewMode.VIEW) {
                                            <td>
                                                <div class="btn-group">
                                                    <button *ngIf="!col._editing" class="btn btn-sm btn-outline-primary" (click)="editColumn(col)"><i class="fa fa-pencil"></i></button>
                                                    <button *ngIf="col._editing" class="btn btn-sm btn-primary" (click)="saveColumn(col)"><i class="fa fa-check"></i></button>
                                                    <button class="btn btn-sm btn-danger" (click)="deleteColumn(col)"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        }
                                    </tr>
                                }
                            }
                        </tbody>
                    </table>
                </div>

                <button *ngIf="viewMode != ViewMode.VIEW" type="button" class="btn btn-sm btn-outline-primary mt-2 mb-3" (click)="addColumn()">
                    <i class="fa fa-plus me-1"></i> Spalte hinzufügen
                </button>

                <div><label class="form-label">Einträge</label></div>
                <div class="table-responsive">
                    <table class="table table-sm">
                        <thead>
                        <tr>
                            @if (viewMode != ViewMode.VIEW) {
                                <th style="width: 100px"></th>
                            }
                            <th style="width: 30px">#</th>
                            @for (col of getActiveColumns(); track col) {
                                <th>{{ col.name || getColumnTypeLabel(col.type) }}</th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                            @for (item of items; track item; let i = $index) {
                                @if (!item._deleted) {
                                    <tr>
                                        @if (viewMode != ViewMode.VIEW) {
                                            <td>
                                                <div class="btn-group">
                                                    <button *ngIf="!item._editing" class="btn btn-sm btn-outline-primary" (click)="editItem(item)"><i class="fa fa-pencil"></i></button>
                                                    <button *ngIf="item._editing" class="btn btn-sm btn-primary" (click)="saveItem(item)"><i class="fa fa-check"></i></button>
                                                    <button class="btn btn-sm btn-danger" (click)="deleteItem(item)"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        }
                                        <td>{{ i + 1 }}</td>
                                        @for (col of getActiveColumns(); track col) {
                                            <td>
                                                @if (item._editing) {
                                                    @if (col.type === 'image') {
                                                        @if (item.values?.[col.position]) {
                                                            <img [src]="getImageUrl(item.values[col.position])" style="max-height: 60px; max-width: 100px; display: block; margin-bottom: 4px">
                                                        }
                                                        <input type="file" class="form-control form-control-sm" accept="image/*" (change)="onFileChange($event, item, col)">
                                                    } @else if (col.type === 'number') {
                                                        <input type="number" class="form-control form-control-sm" [(ngModel)]="item.values[col.position]">
                                                    } @else {
                                                        <input type="text" class="form-control form-control-sm" [(ngModel)]="item.values[col.position]">
                                                    }
                                                } @else {
                                                    @if (col.type === 'image') {
                                                        @if (item.values?.[col.position]) {
                                                            <img [src]="getImageUrl(item.values[col.position])" style="max-height: 60px; max-width: 100px">
                                                        } @else {
                                                            <span class="text-muted">—</span>
                                                        }
                                                    } @else {
                                                        {{ item.values?.[col.position] }}
                                                    }
                                                }
                                            </td>
                                        }
                                    </tr>
                                }
                            }
                        </tbody>
                    </table>
                </div>

                <button *ngIf="viewMode != ViewMode.VIEW" type="button" class="btn btn-sm btn-outline-primary mt-2" (click)="addItem()">
                    <i class="fa fa-plus me-1"></i> Eintrag hinzufügen
                </button>

                <div class="mt-3">
                    @if (viewMode == ViewMode.VIEW) {
                        <button type="button" class="btn btn-primary me-2" (click)="setMode(ViewMode.EDIT)">Bearbeiten</button>
                        <button type="button" class="btn btn-secondary me-2" (click)="cancel()">Zurück</button>
                        <button type="button" class="btn btn-outline-secondary" (click)="export()">
                            <i class="fa fa-file-pdf me-1"></i> PDF exportieren
                        </button>
                        <button type="button" class="btn btn-danger float-end" (click)="delete()">Löschen</button>
                    } @else if (viewMode == ViewMode.EDIT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="update()">Aktualisieren</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancelEdit()">Abbrechen</button>
                    } @else if (viewMode == ViewMode.INSERT) {
                        <button type="button" class="btn btn-primary" [disabled]="form.invalid" (click)="save()">Speichern</button>
                        <button type="button" class="btn btn-secondary ms-2" (click)="cancel()">Abbrechen</button>
                    }
                </div>
            </div>
        </div>
    `
})
export class GenericListDetailComponent implements OnInit {

    accessService = inject(AccessService);
    private router = inject(Router);
    private fb = inject(UntypedFormBuilder);
    private dataService = inject(DataService);
    private route = inject(ActivatedRoute);
    private toastrService = inject(ToastrService);
    private sanitizer = inject(DomSanitizer);

    readonly ViewMode = ViewMode;

    form!: UntypedFormGroup;
    id_list!: string;
    viewMode!: ViewMode;
    list: any;
    items: any[] = [];
    columns: any[] = [];
    dragIndex: number = -1;
    dragOverIndex: number = -1;

    ngOnInit(): void {
        this.id_list = this.route.snapshot.paramMap.get('id') ?? '0';
        this.viewMode = this.id_list == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            name: [null, Validators.required],
            header: [null],
            footer: [null]
        });

        if (this.id_list !== '0') {
            this.getList();
        }
    }

    getList(): void {
        this.dataService.request('GenericList/get', {
            id_list: this.id_list
        }).subscribe(response => {
            this.list = response.list;
            this.items = response.items.map((item: any) => ({
                ...item,
                values: (item.values && !Array.isArray(item.values)) ? item.values : {}
            }));
            this.columns = response.columns;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.list.id,
            name: this.list.name,
            header: this.list.header,
            footer: this.list.footer
        });
    }

    save(): void {
        this.dataService.request('GenericList/save', {
            list: this.form.getRawValue(),
            items: this.items,
            columns: this.columns,
        }).subscribe(() => {
            this.router.navigate(['/list']);
            this.toastrService.success('Die Liste wurde angelegt');
        });
    }

    update(): void {
        this.dataService.request('GenericList/update', {
            list: this.form.getRawValue(),
            items: this.items,
            columns: this.columns,
        }).subscribe(() => {
            this.setMode(ViewMode.VIEW);
            this.getList();
            this.toastrService.success('Die Liste wurde aktualisiert');
        });
    }

    cancel(): void {
        this.router.navigate(['/list']);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Die Liste wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#1f79d3',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('GenericList/delete', {
                    id_list: this.id_list
                }).subscribe(() => {
                    this.cancel();
                    this.toastrService.success('Die Liste wurde gelöscht');
                });
            }
        });
    }

    deleteItem(item: any): void {
        if (!item.id) {
            this.items = this.items.filter(i => i !== item);
        } else {
            item._deleted = true;
        }
    }

    addItem(): void {
        const values: { [key: number]: any } = {};
        this.getActiveColumns().forEach(col => {
            values[col.position] = '';
        });

        this.items.push({
            id: null,
            id_generic_list: null,
            position: this.items.filter(i => !i._deleted).length + 1,
            values,
            _editing: true
        });
    }

    editItem(item: any): void {
        if (!item.values) {
            item.values = {};
        }
        this.getActiveColumns().forEach(col => {
            if (item.values[col.position] === undefined) {
                item.values[col.position] = '';
            }
        });
        item._editing = true;
    }

    saveItem(item: any): void {
        delete item._editing;
    }

    getActiveColumns(): any[] {
        return this.columns.filter(c => !c._deleted);
    }

    onFileChange(event: Event, item: any, col: any): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files[0]) {
            const reader = new FileReader();
            reader.onload = (e) => {
                item.values[col.position] = e.target?.result as string;
            };
            reader.readAsDataURL(input.files[0]);
        }
    }

    getImageUrl(value: string): SafeUrl | string {
        if (!value) return '';
        if (value.startsWith('data:')) {
            return this.sanitizer.bypassSecurityTrustUrl(value);
        }
        return (this.list?.file_path || '') + value;
    }

    addColumn(): void {
        const position = this.columns.filter(c => !c._deleted).length + 1;
        this.columns.push({
            id: null,
            id_generic_list: null,
            position,
            name: '',
            type: 'text',
            _editing: true
        });
    }

    editColumn(col: any): void {
        col._editing = true;
    }

    saveColumn(col: any): void {
        delete col._editing;
    }

    deleteColumn(col: any): void {
        if (!col.id) {
            this.columns = this.columns.filter(c => c !== col);
        } else {
            col._deleted = true;
        }
        this.updateColumnPositions();
    }

    getColumnTypeLabel(type: string): string {
        const labels: { [key: string]: string } = {text: 'Text', number: 'Zahl', image: 'Bild'};
        return labels[type] || type;
    }

    onColumnDragStart(event: DragEvent, index: number): void {
        this.dragIndex = index;
    }

    onColumnDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        this.dragOverIndex = index;
    }

    onColumnDragLeave(): void {
        this.dragOverIndex = -1;
    }

    onColumnDrop(event: DragEvent, index: number): void {
        event.preventDefault();
        if (this.dragIndex !== -1 && this.dragIndex !== index) {
            const moved = this.columns.splice(this.dragIndex, 1)[0];
            this.columns.splice(index, 0, moved);
            this.updateColumnPositions();
        }
        this.dragIndex = -1;
        this.dragOverIndex = -1;
    }

    private updateColumnPositions(): void {
        let pos = 1;
        this.columns.forEach(col => {
            if (!col._deleted) {
                col.position = pos++;
            }
        });
    }

    export(): void {
        Swal.fire({
            title: 'Export-Format wählen',
            input: 'radio',
            inputOptions: {
                'P': 'Hochformat',
                'L': 'Querformat'
            },
            inputValue: 'P',
            showCancelButton: true,
            cancelButtonText: 'Abbrechen',
            confirmButtonText: 'Exportieren',
            confirmButtonColor: this.accessService.client.primaryColor,
            inputValidator: (value) => {
                if (!value) {
                    return 'Sie müssen ein Format wählen!';
                }
                return null;
            }
        }).then(result => {
            if (result.value) {
                this.dataService.request('GenericList/export', {
                    id_list: this.id_list,
                    orientation: result.value
                }).subscribe(response => {
                    if (response?.pdf) {
                        const byteCharacters = atob(response.pdf);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], {type: 'application/pdf'});
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                        setTimeout(() => URL.revokeObjectURL(url), 60000);
                    }
                });
            }
        });
    }
}
